import { useState } from 'react';
import {
  useClassPerfBySubject, useStudentChapterMastery, useStudentCycles, useStudentMissedQuestions,
  useStudentPatterns, useStudentWeeklyTrend,
} from '../../../api/hooks';
import { Chip, EmptyState } from '../../../components/ui';
import { FaChartLine } from '../../../components/ui/icons';
import Box from '../components/Box';
import { chapterRows, subjectRows } from '../lib/mastery';
import { remediationCycleNumber } from '../lib/derive';
import {
  FAILED, LOADING, MasteryLegend, MasteryList, PctBar, SubjectPills, type SubjectKey, pct, pickCaption,
  subjectLabel, useStudentSubjects,
} from './shared';

const THIRD = { first: 'First third of the exam', middle: 'Middle third', last: 'Last third' } as const;
const monthName = (m: string) => new Date(`${m}-01T00:00:00`).toLocaleDateString(undefined, { month: 'short', year: '2-digit' });

export default function GapsView({ id, studentName, cohort }: {
  id: number; studentName: string; cohort?: { gradeId: number | null; sectionId: number | null };
}) {
  const [subject, setSubject] = useState<SubjectKey>('all');
  const subjects = useStudentSubjects(id);
  const subjectName = subjectLabel(subjects, subject);
  const trendQ = useStudentWeeklyTrend(id, { subject: subject === 'all' ? undefined : subject });
  const masteryQ = useStudentChapterMastery(id, { subject });
  const classSubjectsQ = useClassPerfBySubject({ grade: cohort?.gradeId ?? null, section: cohort?.sectionId ?? 'all' });
  const missedQ = useStudentMissedQuestions(id, { subject });
  const cyclesQ = useStudentCycles(id);
  const patternsQ = useStudentPatterns(id, subject);

  const masteryRows = subject === 'all' ? subjectRows(trendQ.data, classSubjectsQ.data?.subjects) : chapterRows(masteryQ.data);
  const masteryLoading = subject === 'all' ? trendQ.isLoading : masteryQ.isLoading;
  const masteryFailed = subject === 'all' ? (trendQ.isError || !trendQ.data) : (masteryQ.isError || !masteryQ.data);
  const missed = (missedQ.data?.questions ?? []).slice(0, 5);
  const openCycle = (cyclesQ.data?.cycles ?? []).filter((c) => c.status !== 'closed').sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
  const p = patternsQ.data;
  const months = (p?.difficulty_by_month ?? []).slice(-6);
  const b = p?.blanks;

  return (
    <div className="space-y-4">
      <SubjectPills id={id} value={subject} onChange={setSubject} note="Filters every block here" />

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Box title={subject === 'all' ? 'Subjects vs class average' : `${subjectName} chapters vs class`} help="chapter_mastery"
          actions={masteryRows.length > 0 ? <MasteryLegend name={studentName} hasClass={masteryRows.some((r) => r.classValue != null)} /> : undefined}>
          {masteryLoading ? LOADING : masteryFailed ? FAILED :
           masteryRows.length === 0 ? <EmptyState compact icon={<FaChartLine />} title="No chapter attempts yet" /> :
           <MasteryList rows={masteryRows} />}
        </Box>

        <Box title="Missed again and again" help="Chapters the student got questions wrong in on two or more different exams: stronger evidence than one bad exam.">
          {patternsQ.isLoading ? LOADING : patternsQ.isError || !p ? FAILED :
           p.repeat_misses.length === 0 ? <p className="text-sm text-muted">No chapter has been missed on more than one exam{subject === 'all' ? '' : ` in ${subjectName}`}.</p> : (
             <ul className="space-y-3">
               {p.repeat_misses.map((c) => (
                 <li key={c.chapter_id}>
                   <div className="mb-1.5 flex justify-between gap-2 text-[13px]">
                     <span className="font-semibold text-ink">{c.name}</span>
                     <span className="font-mono text-ink">{c.exams_missed} exams<span className="text-muted"> · {c.missed}/{c.total} wrong</span></span>
                   </div>
                   <PctBar value={c.pct_missed} tone="danger" label={`${c.name}: ${pct(c.pct_missed)} wrong over ${c.exams_missed} exams`} />
                 </li>
               ))}
             </ul>
           )}
        </Box>
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Box title="Accuracy by difficulty" help="Share right on easy, medium and hard questions, month by month. Low on easy questions points to the basics; low only on hard ones to applying them.">
          {patternsQ.isLoading ? LOADING : patternsQ.isError || !p ? FAILED :
           months.length === 0 ? <p className="text-sm text-muted">No answered questions yet.</p> : (
             <div className="overflow-x-auto">
               <table className="w-full text-[13px]">
                 <thead><tr className="text-left text-xs text-muted"><th className="py-1 font-semibold">Month</th>
                   <th className="py-1 font-semibold">Easy</th><th className="py-1 font-semibold">Medium</th><th className="py-1 font-semibold">Hard</th></tr></thead>
                 <tbody>
                   {months.map((m) => (
                     <tr key={m.month} className="border-t border-hairline">
                       <td className="py-2 font-mono text-xs text-muted">{monthName(m.month)}</td>
                       {(['easy', 'medium', 'hard'] as const).map((lvl) => (
                         <td key={lvl} className="py-2 pr-3">
                           {m[lvl].total === 0 ? <span className="text-muted">—</span> : (
                             <span className="flex items-center gap-2">
                               <span className="w-10 font-mono">{pct(m[lvl].pct)}</span>
                               <span className="flex-1"><PctBar value={m[lvl].pct} tone={lvl === 'easy' ? 'success' : lvl === 'hard' ? 'danger' : 'slate'} label={`${lvl} ${monthName(m.month)}: ${pct(m[lvl].pct)}`} /></span>
                             </span>
                           )}
                         </td>
                       ))}
                     </tr>
                   ))}
                 </tbody>
               </table>
             </div>
           )}
        </Box>

        <Box title="Where blanks fall" help="Unanswered multiple-choice questions by their place in the exam. Blanks bunched at the end usually mean running out of time, not a lack of knowledge.">
          {patternsQ.isLoading ? LOADING : patternsQ.isError || !b ? FAILED :
           b.total === 0 ? <p className="text-sm text-muted">No blank answers on these exams.</p> : (
             <div className="space-y-3">
               {(['first', 'middle', 'last'] as const).map((k) => (
                 <div key={k}>
                   <div className="mb-1.5 flex justify-between text-[13px]"><span className="font-semibold text-ink">{THIRD[k]}</span>
                     <span className="font-mono text-ink">{b[k]}</span></div>
                   <PctBar value={(b[k] / b.total) * 100} tone={k === 'last' && b.end_heavy ? 'danger' : 'slate'} label={`${k} third: ${b[k]} of ${b.total} blanks`} />
                 </div>
               ))}
               {b.end_heavy && <p className="rounded-[12px] bg-warning-bg px-3 py-2 text-[13px] text-warning-ink">Most blanks are at the end: {studentName} may be running out of time. Practice with a timer can help.</p>}
             </div>
           )}
        </Box>
      </div>

      <Box title="Most-missed questions" help="most_missed" actions={<span className="text-xs text-muted">last 30 days</span>}>
        {missedQ.isLoading ? LOADING : missedQ.isError || !missedQ.data ? FAILED :
         missed.length === 0 ? <p className="text-muted">No repeated misses in {subjectName} over the last 30 days.</p> : (
           <ul>
             {missed.map((q) => {
               const caption = pickCaption(q);
               return (
                 <li key={q.id} className="flex items-start gap-3 border-b border-hairline py-3 first:pt-0 last:border-b-0 last:pb-0">
                   <span aria-hidden="true" className="w-1 flex-none self-stretch rounded-sm bg-idle" />
                   <span className="min-w-0 flex-1">
                     <span className="block font-semibold text-ink">{q.ques_excerpt}</span>
                     <span className="mt-[3px] block text-[13px] text-muted">{[q.chapter, caption].filter(Boolean).join(' · ')}</span>
                   </span>
                   <span className="whitespace-nowrap"><Chip tone="down">{q.wrong_count}× wrong</Chip></span>
                 </li>
               );
             })}
           </ul>
         )}
        {openCycle && (
          <div className="mt-3.5 rounded-[14px] bg-accent-bg px-3.5 py-2.5 text-sm font-medium text-accent-ink">
            In remediation · {openCycle.target_chapters[0]?.name ?? openCycle.subject.name}, cycle {remediationCycleNumber(cyclesQ.data?.cycles)}
          </div>
        )}
      </Box>
    </div>
  );
}
